/* ════════════════════════════════════════════════════════════
   PCJ PARIS — JAVASCRIPT THEME v3
   Cart drawer · Swatches · Sticky ATC · Accordions · Menu
════════════════════════════════════════════════════════════ */

'use strict';

/* ── Helpers ── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn) => el && el.addEventListener(ev, fn);

/* ════════════════════════════════════════════════════════════
   CART STATE & DRAWER
════════════════════════════════════════════════════════════ */
const Cart = {
  drawer: null,
  overlay: null,
  inventoryMap: null,
  inventoryFetchPromise: null,

  init() {
    this.drawer  = $('#cart-drawer');
    this.overlay = $('#cart-overlay'); // separate element, sibling to drawer

    on($('#cart-btn'),              'click', () => this.open());
    on(this.overlay,                'click', () => this.close());
    on($('.cart-drawer-close'),     'click', () => this.close());

    // Event delegation for dynamically rendered cart-continue links
    on($('#cart-items'), 'click', e => {
      if (e.target.closest('.cart-continue')) this.close();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });

    this.render();
  },

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.render();
  },

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  },

  async fetch() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  /* Vrai stock des variantes, injecté côté serveur par theme.liquid
     (window.PCJ_INVENTORY) — l'API publique /products/*.js ne renvoie pas
     la quantité exacte, donc on ne peut pas la deviner côté client. */
  getInventoryMap() {
    return (typeof window !== 'undefined' && window.PCJ_INVENTORY) || {};
  },

  async add(variantId, qty = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    });
    const data = await res.json();
    await this.render();
    this.open();
    this.updateCount();
    return data;
  },

  async remove(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    await this.render();
    this.updateCount();
  },

  async changeQty(key, qty) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    });
    await this.render();
    this.updateCount();
  },

  async updateCount() {
    const cart = await this.fetch();
    const count = cart.item_count;
    $$('.cart-count').forEach(el => {
      el.textContent = count > 9 ? '9+' : count;
      el.classList.toggle('visible', count > 0);
    });
  },

  formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  },

  ensureReassuranceRow() {
    const footer = $('.cart-drawer-footer');
    if (!footer || $('.cart-rating-row', footer)) return;
    const trustRow = $('.cart-trust-row', footer);
    const ratingRow = document.createElement('div');
    ratingRow.className = 'cart-rating-row';
    ratingRow.innerHTML = '<span class="cart-rating-stars">★★★★★</span><span>4,9/5 · +168 sportifs équipés</span>';
    if (trustRow) {
      trustRow.parentNode.insertBefore(ratingRow, trustRow);
    } else {
      footer.appendChild(ratingRow);
    }
  },

  async render() {
    const cartItemsEl    = $('#cart-items');
    const cartSubtotalEl = $('#cart-subtotal');
    const shippingFill   = $('#cart-shipping-fill');
    const shippingLabel  = $('#cart-shipping-label');
    if (!cartItemsEl) return;

    const cart = await this.fetch();

    /* Shipping progress bar (free shipping threshold: 49 €) */
    const threshold = 4900;
    const progress  = Math.min(100, (cart.total_price / threshold) * 100);
    if (shippingFill)  shippingFill.style.width = progress + '%';
    if (shippingLabel) {
      if (cart.total_price >= threshold) {
        shippingLabel.innerHTML = '🎉 <strong>Livraison offerte !</strong>';
      } else {
        const remaining = this.formatMoney(threshold - cart.total_price);
        shippingLabel.innerHTML = `Plus que <strong>${remaining}</strong> pour la livraison offerte`;
      }
    }

    /* Subtotal */
    if (cartSubtotalEl) cartSubtotalEl.textContent = this.formatMoney(cart.total_price);

    this.ensureReassuranceRow();

    /* Empty state */
    if (!cart.items.length) {
      cartItemsEl.innerHTML = `
        <div class="cart-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          <p>Votre panier est vide</p>
          <a href="/" class="cart-continue">Commencer mes achats →</a>
        </div>`;
      return;
    }

    /* Stock réel des variantes (jamais un compte à rebours inventé) */
    const inventoryMap = this.getInventoryMap();

    /* Items list */
    cartItemsEl.innerHTML = cart.items.map(item => {
      const stock = inventoryMap[String(item.variant_id)];
      const lowStock = typeof stock === 'number' && stock > 0 && stock <= 10;
      return `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item-img" src="${item.image}" alt="${item.product_title}" loading="lazy">
        <div class="cart-item-info">
          <div class="cart-item-name">${item.product_title}</div>
          ${item.variant_title ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
          ${lowStock ? `<div class="cart-item-stock">⚡ Plus que ${stock} en stock pour ce coloris</div>` : ''}
          <div class="cart-item-row">
            <div class="cart-qty">
              <button class="cart-qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}">−</button>
              <span class="cart-qty-val">${item.quantity}</span>
              <button class="cart-qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}">+</button>
            </div>
            <div class="cart-item-price">${this.formatMoney(item.line_price)}</div>
          </div>
          <div class="cart-item-remove" data-key="${item.key}">Supprimer</div>
        </div>
      </div>
    `;
    }).join('');

    /* Bind item events */
    $$('.cart-item-remove', cartItemsEl).forEach(btn => {
      on(btn, 'click', () => this.remove(btn.dataset.key));
    });
    $$('.cart-qty-btn', cartItemsEl).forEach(btn => {
      on(btn, 'click', () => {
        const current = parseInt(btn.dataset.qty);
        const newQty  = btn.dataset.action === 'increase' ? current + 1 : Math.max(0, current - 1);
        this.changeQty(btn.dataset.key, newQty);
      });
    });
  }
};

/* ════════════════════════════════════════════════════════════
   PRODUCT FORM — SWATCHES + ATC
════════════════════════════════════════════════════════════ */
const ProductForm = {
  selectedVariantId: null,
  selectedColor: null,

  init() {
    const form = $('#product-form');
    if (!form) return;

    this.selectedVariantId = form.dataset.defaultVariant;
    this.selectedColor     = form.dataset.defaultColor || '';

    /* Swatches */
    $$('.swatch').forEach(sw => {
      on(sw, 'click', () => this.selectSwatch(sw));
      sw.setAttribute('tabindex', '0');
      sw.setAttribute('role', 'radio');
      on(sw, 'keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.selectSwatch(sw); }
      });
    });

    /* Thumbnails */
    $$('.product-thumb').forEach(th => {
      on(th, 'click', () => this.selectThumb(th));
    });

    /* Quantity */
    const qtyInput = $('#qty-input');
    on($('#qty-minus'), 'click', () => {
      if (qtyInput && parseInt(qtyInput.value) > 1) qtyInput.value = parseInt(qtyInput.value) - 1;
    });
    on($('#qty-plus'), 'click', () => {
      if (qtyInput) qtyInput.value = parseInt(qtyInput.value) + 1;
    });

    /* ATC */
    on(form, 'submit', e => { e.preventDefault(); this.addToCart(); });
    on($('#sticky-atc-btn'), 'click', () => this.addToCart());
  },

  selectSwatch(el) {
    $$('.swatch').forEach(s => { s.classList.remove('active'); s.setAttribute('aria-checked', 'false'); });
    el.classList.add('active');
    el.setAttribute('aria-checked', 'true');

    this.selectedVariantId = el.dataset.variantId;
    this.selectedColor     = el.dataset.color;

    /* Update color label */
    const colorLabel = $('#selected-color');
    if (colorLabel) colorLabel.textContent = this.selectedColor;

    /* Swap main image */
    const imgSrc = el.dataset.imgSrc;
    if (imgSrc) {
      const mainImg = $('#main-product-img');
      if (mainImg) {
        mainImg.classList.add('swapping');
        setTimeout(() => {
          mainImg.src = imgSrc;
          mainImg.classList.remove('swapping');
        }, 180);
      }
      $$('.product-thumb').forEach(th => {
        th.classList.toggle('active', th.dataset.imgSrc === imgSrc);
      });
    }

    /* Update sticky price */
    const price = el.dataset.price;
    if (price) {
      const stickyPrice = $('#sticky-price');
      if (stickyPrice) stickyPrice.textContent = this.formatPrice(parseInt(price));
    }
  },

  selectThumb(el) {
    $$('.product-thumb').forEach(th => th.classList.remove('active'));
    el.classList.add('active');
    const mainImg = $('#main-product-img');
    if (mainImg && el.dataset.imgSrc) {
      mainImg.classList.add('swapping');
      setTimeout(() => {
        mainImg.src = el.dataset.imgSrc;
        mainImg.classList.remove('swapping');
      }, 180);
    }
  },

  formatPrice(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  },

  async addToCart() {
    if (!this.selectedVariantId) return;
    const atcBtn   = $('#atc-btn');
    const qtyInput = $('#qty-input');
    const qty      = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

    if (atcBtn) {
      atcBtn.classList.add('loading');
      atcBtn.innerHTML = '<div class="loading-spinner"></div>';
    }

    try {
      await Cart.add(this.selectedVariantId, qty);
      Notification.show('✓', 'Ajouté au panier', this.selectedColor || 'Lunettes PCJ Paris');
    } catch (e) {
      console.error(e);
    } finally {
      if (atcBtn) {
        atcBtn.classList.remove('loading');
        atcBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg> Ajouter au panier';
      }
    }
  }
};

/* ════════════════════════════════════════════════════════════
   ACCORDIONS (product page)
════════════════════════════════════════════════════════════ */
function initAccordions() {
  /* Product accordions */
  $$('.accordion-btn').forEach(btn => {
    on(btn, 'click', () => {
      const item   = btn.closest('.accordion-item');
      const isOpen = item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(isOpen));
      const body = item.querySelector('.accordion-body');
      if (body) body.style.maxHeight = isOpen ? body.scrollHeight + 'px' : '0';
    });
  });

  /* Open any item that starts with is-open class */
  $$('.accordion-item.is-open').forEach(item => {
    const btn  = item.querySelector('.accordion-btn');
    const body = item.querySelector('.accordion-body');
    if (btn)  btn.setAttribute('aria-expanded', 'true');
    if (body) body.style.maxHeight = body.scrollHeight + 'px';
  });

  /* FAQ */
  $$('.faq-q').forEach(q => {
    on(q, 'click', () => {
      const item   = q.closest('.faq-item');
      const isOpen = item.classList.toggle('is-open');
      q.setAttribute('aria-expanded', String(isOpen));
      const body = item.querySelector('.faq-a');
      if (body) body.style.maxHeight = isOpen ? body.scrollHeight + 'px' : '0';
    });
  });
}

/* ════════════════════════════════════════════════════════════
   MOBILE MENU
════════════════════════════════════════════════════════════ */
function initMobileMenu() {
  const menu   = $('#mobile-menu');
  const toggle = $('#menu-toggle');
  if (!menu || !toggle) return;

  const closeBtn = menu.querySelector('.mobile-menu-close');

  const openMenu = () => {
    menu.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    toggle.setAttribute('aria-expanded', 'true');
    if (closeBtn) closeBtn.focus();
  };
  const closeMenu = () => {
    menu.classList.remove('is-open');
    document.body.style.overflow = '';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.focus();
  };

  on(toggle,                                     'click', openMenu);
  on(menu.querySelector('.mobile-menu-overlay'), 'click', closeMenu);
  on(closeBtn,                                   'click', closeMenu);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu();
  });
}

/* ════════════════════════════════════════════════════════════
   STICKY ATC (show when desktop ATC scrolls out of view)
════════════════════════════════════════════════════════════ */
function initStickyAtc() {
  const atcBtn   = $('#atc-btn');
  const stickyAtc = $('.sticky-atc');
  if (!atcBtn || !stickyAtc) return;

  const observer = new IntersectionObserver(([entry]) => {
    stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
  }, { threshold: 0 });
  observer.observe(atcBtn);
}

/* ════════════════════════════════════════════════════════════
   SCROLL TO TOP
════════════════════════════════════════════════════════════ */
function initScrollToTop() {
  const btn = document.getElementById('scroll-top-btn');
  if (!btn) return;

  window.addEventListener('scroll', () => {
    btn.classList.toggle('is-visible', window.scrollY > 400);
  }, { passive: true });

  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

/* ════════════════════════════════════════════════════════════
   TOAST NOTIFICATION
════════════════════════════════════════════════════════════ */
const Notification = {
  el: null,
  timeout: null,

  init() {
    this.el = $('#toast');
  },

  show(icon, title, desc) {
    if (!this.el) return;
    this.el.querySelector('.toast-icon').textContent = icon;
    this.el.querySelector('.toast-text').innerHTML   = `<strong>${title}</strong> — ${desc}`;
    this.el.classList.add('is-visible');
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.el.classList.remove('is-visible'), 3000);
  }
};

/* ════════════════════════════════════════════════════════════
   INIT
════════════════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  Cart.init();
  Cart.updateCount();
  ProductForm.init();
  initAccordions();
  initMobileMenu();
  initStickyAtc();
  initScrollToTop();
  Notification.init();
  window.PCJ = { openCart: () => Cart.open() };
});